import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { addDays, formatISO, parseISO } from "date-fns";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { entityTypeItems } from "@components/onboarding/constants";
import { EntityProfileType, EntityProfileIndustry } from "@common/types/entity";
import { IndustryPicker } from "@components/onboarding/components/IndustryPicker";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useAutofocus } from "@hooks/useAutofocus";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { OrganizationServicesOffered } from "@common/types/organization";
import {
    SERVICE_OPTIONS,
    servicesOfferedLabels,
} from "@components/entity/constants";
import { getMultiSelectTriggerValue } from "@components/onboarding/lib";
import { WorkspaceTimezoneField } from "@components/onboarding/components/WorkspaceTimezoneField";
import { AddClientMode, ClientFormSchema } from "./lib";
import styles from "./ClientStep.module.scss";

const fields = createFormikFields<ClientFormSchema>();

interface Props {
    mode: AddClientMode;
}

export const ClientStep = ({ mode }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { values, setFieldValue } = useFormikContext<ClientFormSchema>();
    const { newAddClientFlow } = useTypedFlags();

    const migrationEndDate = values.historicalData.endDate;
    const isStartDateDerived =
        values.historicalData.migrate && !!migrationEndDate;

    useEffect(() => {
        if (!isStartDateDerived || !migrationEndDate) {
            return;
        }
        try {
            const derived = formatISO(addDays(parseISO(migrationEndDate), 1), {
                representation: "date",
            });
            if (values.client.bookkeepingStartDate !== derived) {
                setFieldValue("client.bookkeepingStartDate", derived);
            }
        } catch {
            // ignore invalid dates
        }
    }, [
        isStartDateDerived,
        migrationEndDate,
        values.client.bookkeepingStartDate,
        setFieldValue,
    ]);

    useAutofocus(inputRef);

    const isLegalTypeLocked = values.coaSetupMethod === "template";

    return (
        <>
            {mode === "newClient" && (
                <fields.Text
                    size="xl"
                    name="client.clientName"
                    label="Client name (displayed on clients tab)"
                    ref={inputRef}
                    className={styles.inputField}
                />
            )}

            <fields.Text
                size="xl"
                name="client.businessName"
                label="Business name"
                className={styles.inputField}
            />

            <FormikField<EntityProfileType | null>
                name="client.legalType"
                label="Entity type"
                control="button"
                className={styles.buttonField}
            >
                {({ helpers, field, invalid }) => (
                    <Tooltip
                        content="The Chart of Accounts template sets the entity type."
                        disabled={!isLegalTypeLocked}
                    >
                        <span className={styles.lockedPickerTrigger}>
                            <Picker.Select
                                items={entityTypeItems}
                                value={field.value ?? null}
                                onValueChange={(value) =>
                                    helpers.setValue(value)
                                }
                                disabled={isLegalTypeLocked}
                                placeholder="Select"
                                aria-invalid={invalid}
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="xl"
                                        className="mb-2"
                                        block
                                    />
                                }
                            />
                        </span>
                    </Tooltip>
                )}
            </FormikField>

            {newAddClientFlow && (
                <>
                    <IndustryPicker
                        industriesFieldName="client.industries"
                        industryOtherFieldName="client.industryOther"
                        optional
                        triggerClassName="mb-2"
                    />

                    {(values.client.industries ?? []).includes(
                        EntityProfileIndustry.OTHER,
                    ) && (
                        <fields.Text
                            size="xl"
                            name="client.industryOther"
                            label="Other industry"
                            optional
                            className={styles.inputField}
                        />
                    )}
                </>
            )}

            <FormikField<OrganizationServicesOffered[]>
                name="client.servicesOffered"
                label="Practices"
                control="button"
                className={styles.buttonField}
            >
                {({ helpers, field, invalid }) => (
                    <Picker.Select
                        multiple
                        items={SERVICE_OPTIONS}
                        value={field.value ?? []}
                        onValueChange={(value) => helpers.setValue(value)}
                        renderValue={(selectedServices) =>
                            getMultiSelectTriggerValue(
                                [...selectedServices],
                                SERVICE_OPTIONS.length,
                                (v) => servicesOfferedLabels[v],
                            )
                        }
                        placeholder="Select"
                        aria-invalid={invalid}
                        trigger={
                            <Picker.Button
                                variant="primary"
                                size="xl"
                                className="mb-2"
                            />
                        }
                    />
                )}
            </FormikField>

            <fields.Date
                name="client.bookkeepingStartDate"
                label="Sync date"
                showErrorMessage={false}
                variant="panel-input"
                pickerLabel="Sync date"
                disabled={isStartDateDerived}
                placeholder="Select"
                buttonProps={{
                    variant: "primary",
                    size: "xl",
                    block: true,
                }}
            />

            <p className={styles.info}>
                {isStartDateDerived &&
                    "Set to the day after the migration end date. "}
                Kick starts categorizing transactions from this day. It
                doesn&apos;t affect your pricing.
            </p>

            {mode === "newClient" && (
                <WorkspaceTimezoneField name="client.timezone" />
            )}
        </>
    );
};
